import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Photo {
  _id: string;
  title: string;
  description: string;
  project: string | { _id: string; name: string };
  category: 'progress' | 'quality' | 'safety' | 'before' | 'after' | 'issue' | 'completion' | 'inspection';
  location: string;
  tags: string[];
  filename: string;
  originalName: string;
  mimetype: string;
  size: number;
  path: string;
  url: string;
  thumbnailUrl?: string;
  uploadedBy: string | { _id: string; name: string; email: string };
  createdAt: string;
  updatedAt: string;
  metadata?: {
    width?: number;
    height?: number;
    exif?: any;
    gpsCoordinates?: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface PhotoFilters {
  search?: string;
  project?: string;
  category?: string;
  tags?: string;
  uploadedBy?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PhotoStats {
  totalPhotos: number;
  totalSize: number;
  avgSize: number;
  uniqueUploaders: number;
}

export interface PhotoCategoryStats {
  _id: string;
  count: number;
  totalSize: number;
}

export interface PhotoDailyStats {
  _id: {
    year: number;
    month: number;
    day: number;
  };
  count: number;
}

export interface PhotoStatsResponse {
  overview: PhotoStats;
  byCategory: PhotoCategoryStats[];
  dailyUpload: PhotoDailyStats[];
}

export interface PhotoUploadData {
  title: string;
  description?: string;
  project: string;
  category: 'progress' | 'quality' | 'safety' | 'before' | 'after' | 'issue' | 'completion' | 'inspection';
  location?: string;
  tags?: string[];
  company?: string;
}

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class PhotoDocumentationService {
  // Get all photos
  async getPhotos(page = 1, limit = 20, filters: PhotoFilters = {}) {
    const params = {
      page,
      limit,
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get photo by ID
  async getPhotoById(id: string) {
    const response = await api.get(`/photo-documentation/${id}`);
    return response.data;
  }

  // Upload new photo
  async uploadPhoto(file: File, data: PhotoUploadData) {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('title', data.title);
    formData.append('project', data.project);
    formData.append('category', data.category);
    if (data.company) formData.append('company', data.company);
    
    if (data.description) formData.append('description', data.description);
    if (data.location) formData.append('location', data.location);
    if (data.tags && data.tags.length > 0) {
      formData.append('tags', data.tags.join(','));
    }
    
    const response = await api.post('/photo-documentation', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Update photo
  async updatePhoto(id: string, data: Partial<PhotoUploadData>) {
    const response = await api.put(`/photo-documentation/${id}`, data);
    return response.data;
  }

  // Delete photo
  async deletePhoto(id: string) {
    const response = await api.delete(`/photo-documentation/${id}`);
    return response.data;
  }

  // Get photos by project
  async getPhotosByProject(projectId: string, category?: string, limit = 50, sortBy = 'createdAt', sortOrder = 'desc') {
    const params: any = { limit, sortBy, sortOrder };
    if (category) params.category = category;
    
    const response = await api.get(`/photo-documentation/project/${projectId}`, { params });
    return response.data;
  }

  // Get photos by category
  async getPhotosByCategory(category: string, project?: string, limit = 50, sortBy = 'createdAt', sortOrder = 'desc') {
    const params: any = { limit, sortBy, sortOrder };
    if (project) params.project = project;
    
    const response = await api.get(`/photo-documentation/category/${category}`, { params });
    return response.data;
  }

  // Bulk upload photos
  async bulkUploadPhotos(files: File[], commonData: Partial<PhotoUploadData>) {
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append('photos', file);
    });
    
    // Add common data
    if (commonData.project) formData.append('project', commonData.project);
    if (commonData.category) formData.append('category', commonData.category);
    if (commonData.location) formData.append('location', commonData.location);
    if (commonData.tags && commonData.tags.length > 0) {
      formData.append('tags', commonData.tags.join(','));
    }
    if (commonData.company) formData.append('company', commonData.company);
    
    const response = await api.post('/photo-documentation/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Get photo statistics
  async getPhotoStats(project?: string, startDate?: string, endDate?: string): Promise<PhotoStatsResponse> {
    const params: any = {};
    if (project) params.project = project;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await api.get('/photo-documentation/stats/overview', { params });
    return response.data;
  }

  // Add tags to photo
  async addTagsToPhoto(id: string, tags: string[]) {
    const response = await api.patch(`/photo-documentation/${id}/tags`, { tags });
    return response.data;
  }

  // Remove tags from photo
  async removeTagsFromPhoto(id: string, tags: string[]) {
    const response = await api.delete(`/photo-documentation/${id}/tags`, { 
      data: { tags }
    });
    return response.data;
  }

  // Search photos
  async searchPhotos(query: string, filters: PhotoFilters = {}) {
    const params = {
      search: query,
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get photos by tags
  async getPhotosByTags(tags: string[], filters: PhotoFilters = {}) {
    const params = {
      tags: tags.join(','),
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get photos by date range
  async getPhotosByDateRange(startDate: string, endDate: string, filters: PhotoFilters = {}) {
    const params = {
      startDate,
      endDate,
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get photos by location
  async getPhotosByLocation(location: string, filters: PhotoFilters = {}) {
    const params = {
      location,
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get photos by uploader
  async getPhotosByUploader(uploaderId: string, filters: PhotoFilters = {}) {
    const params = {
      uploadedBy: uploaderId,
      ...filters,
    };
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Export photos data
  async exportPhotosData(filters: PhotoFilters = {}, format = 'csv') {
    const params = {
      ...filters,
      format,
    };
    
    const response = await api.get('/photo-documentation/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  // Download photo
  async downloadPhoto(id: string) {
    const response = await api.get(`/photo-documentation/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Get photo thumbnail
  async getPhotoThumbnail(id: string) {
    const response = await api.get(`/photo-documentation/${id}/thumbnail`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Update photo metadata
  async updatePhotoMetadata(id: string, metadata: any) {
    const response = await api.patch(`/photo-documentation/${id}/metadata`, { metadata });
    return response.data;
  }

  // Get recent photos
  async getRecentPhotos(limit = 10, project?: string) {
    const params: any = { limit, sortBy: 'createdAt', sortOrder: 'desc' };
    if (project) params.project = project;
    
    const response = await api.get('/photo-documentation', { params });
    return response.data;
  }

  // Get popular tags
  async getPopularTags(limit = 20, project?: string) {
    const params: any = { limit };
    if (project) params.project = project;
    
    const response = await api.get('/photo-documentation/tags/popular', { params });
    return response.data;
  }
}

export const photoDocumentationService = new PhotoDocumentationService();
export default photoDocumentationService;